import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
} from 'react';
import { Text } from '@rsos/base-components';
import { Box } from '@rsos/components/capstone/base';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import { Label } from '../../../../AirAmbulanceForm/AirAmbulanceForm/AirAmbulanceFormStandalone.styles';
import {
  SuggestionsContainer,
  SuggestionsList,
} from '../../../../AirAmbulanceForm/AirAmbulanceForm/components/AddressSearchInput.styled';
import {
  SuggestedAddress,
  type GeocodedAddress,
} from '../../../../AirAmbulanceForm/AirAmbulanceForm/components/SuggestedAddress';

export const TOWPRO_ADDRESS_FIELD = 'address';

interface TowProAddressFieldProps {
  value: string;
  error?: string;
  geocodedResults?: GeocodedAddress[];
  isReverseGeocoding: boolean;
  onAddressChange: (value: string) => void;
  onSelectSuggestion: (suggestion: GeocodedAddress) => void;
  onBlur: (e: FocusEvent<HTMLInputElement>) => void;
}

function TowProAddressField({
  value,
  error,
  geocodedResults,
  isReverseGeocoding,
  onAddressChange,
  onSelectSuggestion,
  onBlur,
}: TowProAddressFieldProps): JSX.Element {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onAddressChange(e.target.value);
    setShowSuggestions(Boolean(e.target.value));
    setSelectedSuggestionIndex(-1);
  };

  const handleSelectSuggestion = (suggestion: GeocodedAddress): void => {
    onSelectSuggestion(suggestion);
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Box pb={3}>
      <Label>
        <Text size="small" color="primaryText">
          Address*
        </Text>
      </Label>
      <SuggestionsContainer ref={suggestionsRef}>
        <InputText
          id={TOWPRO_ADDRESS_FIELD}
          name={TOWPRO_ADDRESS_FIELD}
          type="text"
          shouldErrorTakeSpace
          placeholder="Search for pickup address"
          setFieldValue={async () => {
            onAddressChange('');
          }}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          error={error}
          style={{ fontSize: '14px' }}
        />
        {showSuggestions && geocodedResults && geocodedResults.length > 0 && (
          <SuggestionsList>
            {geocodedResults.map((result, index) => (
              <SuggestedAddress
                key={index}
                isSelected={index === selectedSuggestionIndex}
                index={index}
                address={result}
                handleSelectSuggestion={handleSelectSuggestion}
                setSelectedSuggestionIndex={indexOrEvent => {
                  if (typeof indexOrEvent === 'number') {
                    setSelectedSuggestionIndex(indexOrEvent);
                  }
                }}
              />
            ))}
          </SuggestionsList>
        )}
      </SuggestionsContainer>
      {isReverseGeocoding && (
        <Text size="small" color="secondaryText">
          Finding address...
        </Text>
      )}
    </Box>
  );
}

export default TowProAddressField;
